/**
 * AuthContext
 * React context for managing authentication state supporting both
 * Siemens Microsoft Entra ID (Azure AD) via MSAL and local admin login
 * Feature: User Management & Entra ID Integration
 */

import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { loginRequest } from '../auth/msalConfig.js';
import AuthService from '../services/AuthService.js';
import { API_BASE_URL } from '../services/ApiService.js';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [authError, setAuthError] = useState(null);

  /**
   * Acquire active access token (ID token for Entra ID or local JWT)
   */
  const getAccessToken = useCallback(async () => {
    const account = instance.getActiveAccount() || accounts[0];
    if (account) {
      try {
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
        const idToken = result.idToken;
        if (idToken) {
          localStorage.setItem('authToken', idToken);
          setToken(idToken);
          return idToken;
        }
      } catch (err) {
        console.warn('MSAL silent token acquisition failed:', err);
      }
    }

    // Fallback to local stored token
    const stored = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    return stored || null;
  }, [instance, accounts]);

  /**
   * Sync Entra ID user session with backend
   */
  const syncEntraUser = useCallback(async (idToken) => {
    try {
      setAuthError(null);
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        const userData = data.data || data;
        setUser(userData);
        setToken(idToken);
        localStorage.setItem('authToken', idToken);

        try {
          const decoded = jwtDecode(idToken);
          if (decoded.exp) {
            setSessionExpiresAt(new Date(decoded.exp * 1000));
          }
        } catch {
          // Token decode fallback
        }
        return userData;
      } else if (res.status === 403) {
        const errData = await res.json().catch(() => ({}));
        const message = errData.error || 'Your Siemens account is authenticated, but not assigned an admin role in Learning Day.';
        setAuthError(message);
        setUser(null);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error('Error verifying Entra ID user with backend:', err);
      setAuthError(err.message || 'Failed to communicate with authentication server.');
      setUser(null);
    }
    return null;
  }, []);

  /**
   * Restore local session from storage
   */
  const restoreLocalSession = useCallback((storedToken) => {
    try {
      const decoded = jwtDecode(storedToken);
      const now = Date.now() / 1000;

      if (!decoded.exp || decoded.exp <= now) {
        localStorage.removeItem('authToken');
        setUser(null);
        setToken(null);
        return;
      }

      const isEntra = decoded.iss?.includes('microsoft') || decoded.iss?.includes('login.microsoftonline.com');
      if (isEntra) {
        syncEntraUser(storedToken);
      } else {
        setToken(storedToken);
        setUser({
          id: decoded.id,
          username: decoded.username,
          role: decoded.role,
          segment: decoded.segment,
          authType: 'local',
        });
        setSessionExpiresAt(new Date(decoded.exp * 1000));
      }
    } catch (error) {
      console.error('Error decoding stored token:', error);
      localStorage.removeItem('authToken');
    }
  }, [syncEntraUser]);

  // Handle MSAL and Stored Token Authentication State Changes
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (isMsalAuthenticated && accounts.length > 0) {
      const account = instance.getActiveAccount() || accounts[0];
      if (account) instance.setActiveAccount(account);

      getAccessToken().then(async (idToken) => {
        if (idToken) {
          await syncEntraUser(idToken);
        }
        setLoading(false);
      });
      return;
    }

    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      restoreLocalSession(storedToken);
    }
    setLoading(false);
  }, [inProgress, isMsalAuthenticated, accounts, instance, getAccessToken, syncEntraUser, restoreLocalSession]);

  /**
   * Login with Microsoft Entra ID (Redirect flow)
   */
  const loginWithEntra = useCallback(async (redirectPath = '/admin') => {
    setAuthError(null);
    sessionStorage.setItem('auth_redirect', redirectPath);
    await instance.loginRedirect({
      ...loginRequest,
      state: redirectPath,
    });
  }, [instance]);

  /**
   * Login with Microsoft Entra ID (Popup flow)
   */
  const loginWithEntraPopup = useCallback(async () => {
    setAuthError(null);
    try {
      const result = await instance.loginPopup(loginRequest);
      if (result?.account) {
        instance.setActiveAccount(result.account);
      }
      if (result?.idToken) {
        const userData = await syncEntraUser(result.idToken);
        return userData;
      }
    } catch (err) {
      console.error('MSAL popup login error:', err);
      setAuthError(err.message || 'Microsoft login was cancelled or failed.');
      throw err;
    }
  }, [instance, syncEntraUser]);

  /**
   * Local username & password login
   */
  const login = async (username, password) => {
    setAuthError(null);
    try {
      const response = await AuthService.login(username, password);
      const { token: localToken, user: userData } = response;

      localStorage.setItem('authToken', localToken);
      setToken(localToken);
      setUser({ ...userData, authType: 'local' });

      const decoded = jwtDecode(localToken);
      if (decoded.exp) {
        setSessionExpiresAt(new Date(decoded.exp * 1000));
      }

      return response;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  };

  /**
   * Logout function (handles both MSAL and local sessions)
   */
  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('auth_redirect');
    setToken(null);
    setUser(null);
    setSessionExpiresAt(null);
    setAuthError(null);

    const activeAccount = instance.getActiveAccount() || (accounts.length > 0 ? accounts[0] : null);
    if (activeAccount) {
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/login',
      });
    }
  }, [instance, accounts]);

  /**
   * Refresh token
   */
  const refreshToken = (newToken) => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);

    try {
      const decoded = jwtDecode(newToken);
      if (decoded.exp) {
        setSessionExpiresAt(new Date(decoded.exp * 1000));
      }
    } catch {
      // Ignore
    }
  };

  const hasRole = (role) => user?.role === role;
  const isSuperAdmin = () => user?.role === 'super_admin';
  const isAdmin = () => user?.role === 'admin' || user?.role === 'super_admin';

  const canAccessSegment = (segment) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (user.role === 'admin') return user.segment === segment;
    return false;
  };

  const getAccessibleSegments = () => {
    if (!user) return [];
    if (user.role === 'super_admin') {
      return ['OPS-SW', 'Grow@PLM', 'LCS', 'ME', 'PES', 'SIM'];
    }
    if (user.role === 'admin' && user.segment) {
      return [user.segment];
    }
    return [];
  };

  const value = useMemo(() => ({
    user,
    token,
    loading,
    authError,
    sessionExpiresAt,
    isAuthenticated: !!user,
    login,
    loginWithEntra,
    loginWithEntraPopup,
    logout,
    getAccessToken,
    refreshToken,
    hasRole,
    isSuperAdmin,
    isAdmin,
    canAccessSegment,
    getAccessibleSegments,
  }), [
    user,
    token,
    loading,
    authError,
    sessionExpiresAt,
    loginWithEntra,
    loginWithEntraPopup,
    logout,
    getAccessToken,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
